import { useEffect, useRef } from 'react';
import { Logo } from '../components/Logo';

/**
 * Google sign-in gate. Renders the GSI button (shared web client id) and hands
 * the returned credential up to useAuth().loginWithGoogle, which exchanges it
 * at staff-api /auth/token. Only shown when VITE_STAFF_AUTH_DISABLED !== 'true'
 * (App decides). If the client id is unset we surface a config hint rather than
 * a blank card.
 */
declare global {
  interface Window {
    google?: any;
  }
}

const CLIENT_ID = import.meta.env.VITE_STAFF_SSO_GOOGLE_CLIENT_ID || '';

export function LoginPage({
  onCredential,
}: {
  onCredential: (idToken: string) => Promise<void>;
}) {
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: (resp: { credential: string }) => {
          void onCredential(resp.credential);
        },
      });
      if (btnRef.current) {
        window.google?.accounts.id.renderButton(btnRef.current, {
          theme: 'filled_black',
          size: 'large',
        });
      }
    };
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, [onCredential]);

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-brand">
          <Logo variant="hero" />
        </div>
        {/* One line on what this actually is. A sign-in card that only says
            "sign in" tells a first-time visitor nothing about what they're
            signing in to. */}
        <p className="login-lede">
          The inventory of record for OptiSigns infrastructure — repos, clusters, secrets (names
          only) and deploy catalogs, for people and for AI agents.
        </p>
        {CLIENT_ID ? (
          <>
            <div ref={btnRef} style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }} />
            {/* Stated BEFORE the attempt: a personal Google account fails at the
                issuer with nothing on screen explaining why, which reads as the
                app being broken. */}
            <p className="muted text-2xs" style={{ marginTop: 16 }}>
              Use your <strong>@optisigns.com</strong> account. Personal Google accounts are
              refused, and any signed-in staff member gets in — a role is granted afterwards.
            </p>
          </>
        ) : (
          // Config hint for a developer, not for a customer: this branch only
          // renders when the build has no client id, which never happens in a
          // deployed environment.
          <p className="muted" style={{ marginTop: 20 }}>
            Google sign-in isn&apos;t configured for this build. For local dev set{' '}
            <code>VITE_STAFF_AUTH_DISABLED=true</code>, or set{' '}
            <code>VITE_STAFF_SSO_GOOGLE_CLIENT_ID</code>.
          </p>
        )}
      </div>
    </div>
  );
}
